<script setup lang="ts">
import { computed, ref, toRef, markRaw, defineAsyncComponent, onUnmounted, StyleValue, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import { useInventoryStore } from '../../pages/useInventoryStore/';
import { InventoryTableViewModel } from '../../pages/inventoryTableViewModel';
import { DateService } from '@/coreDomainsModule/services/dateService';
import SvgLoader from '@/utils/SvgLoader.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import TableMemoButton from '@/corePagesModule/tableMemoButton/TableMemoButton.vue';
import ArrowIcon from '@/corePagesModule/BaseTableContent/components/ArrowIcon.vue';
import {
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  DETAIL_BUTTON_COLUMN_WIDTH,
  HEADER_MODIFY_BUTTON_COLUMN_WIDTH,
} from '@/corePagesModule/BaseTableContent/constants';
import { VIRTUAL_SCROLL } from '../../constants/tableConstants';
import { getMonthFromHeaderKey } from '../../utils/tableHelpers';
import { TABLE_CONTENT_ID, HEADER_COLUMN_DEFAULT_NUMBER } from '../../constants';
import { IParentBusiness } from '../../domains/entities/Business';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { isLoading, isZeroMatch } from '@/coreDomainsModule/uiLoadingState';
import { InventoryTableService } from '../../domains/services/inventoryTableService';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';

// 仮想スクロールコンポーネントをインポート
import VirtualizedEzTable from '@/corePagesModule/components/ezTable/VirtualizedEzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzTableLoadingState from '@/corePagesModule/components/ezTable/EzTableLoadingState.vue';
import EzTableErrorState from '@/corePagesModule/components/ezTable/EzTableErrorState.vue';
import EzTableZeroMatchState from '@/corePagesModule/components/ezTable/EzTableZeroMatchState.vue';
// 非同期読み込みでバンドルサイズを削減
const InventoryMonthlyColumn = defineAsyncComponent(() => import('./InventoryMonthlyColumn.vue'));
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { useTableMemo } from '../../composables/useTableMemo';
import { useTableCalculation } from '@/InventoryControlModule/composables/useTableCalculation';
import { TableCommonViewModel, TableContentViewModel } from '@/viewModels/tableContent';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { useTableState } from '../../composables/useTableState';
import TransactionSummaryColumn from './TransactionSummaryColumn.vue';
import BusinessRowMenu from './BusinessRowMenu.vue';
import { IEditTransactionValue } from '@/InventoryControlModule/pages/useInventoryStore/type';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';

/**
 * VirtualizedEzInventoryTableコンポーネントのプロパティ
 * @property {number} tableContainerWidth - テーブルコンテナの幅
 * @property {number} tableContainerHeight - テーブルコンテナの高さ
 * @property {boolean} isEllipsis - テキストが省略されているかどうか
 * @property {Function} mouseoverTextLabel - マウスオーバー時のテキストラベル処理関数
 */
interface VirtualizedEzInventoryTableProps {
  tableContainerWidth: number;
  tableContainerHeight: number;
  isEllipsis: boolean;
  transactionMonths: number[];
  mouseoverTextLabel: (e: MouseEvent) => void;
}

// Props定義
const props = defineProps<VirtualizedEzInventoryTableProps>();

// Store
const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const {
  toggleOpenBusinesses,
  onCheckedAllRows,
  changeHeaderSorting,
  fetchState,
  fetchChildBusinesses,
  setBusinessRowMenuStateAndProcessing,
  getHiddenTransactionHeader,
} = inventoryStore;
const { checkNearestRecommend, getArrivalStatusWhenDisplayButton, getHiddenHeaderNumber } = useTableCalculation();

// Memo機能のComposable
const { saveAggregateMemo, deleteAggregateMemo, saveChildMemo, deleteChildMemo } = useTableMemo();

// テーブル状態管理のComposable
const { tableTextStyleClass, tableNumberStyleClass, getNowInventoryStatusClass, isNegative } = useTableState();

// i18n
const t = i18n.t;

const vm = markRaw(new TableCommonViewModel());
const contentVm = markRaw(new TableContentViewModel());
const inventoryVm = markRaw(new InventoryTableViewModel());

/**
 * テーブル表示用に空値を`--`へ変換する
 * @param value - 表示対象の値
 */
const displayOrDash = (value: string | number | undefined | null): string | number => {
  if (value == null) {
    return '--';
  }
  if (typeof value === 'string' && value.trim() === '') {
    return '--';
  }
  return value;
};

// テーブル要素のrefを宣言（useColumnResizerより前に）
const tableEl = ref<HTMLElement | null>(null);

// 仮想スクロール設定（定数化でcomputed計算を回避）
const ITEM_HEIGHT = VIRTUAL_SCROLL.ITEM_HEIGHT;
const BUFFER_SIZE = VIRTUAL_SCROLL.BUFFER_SIZE;

/**
 * 動的に計算されるコンテナ高さ（propsから取得）
 */
const containerHeight = computed(() => props.tableContainerHeight || 1000);

// 処理負荷軽減のためのキャッシュマップ
const styleClassCache = new Map<string, string>();
const headerDisplayCache = new Map<string, string>();
const executionDateCache = new Map<string, string>();

/**
 * テーブルのヘッダ（チェックボックス列を含む完全版）
 * @property {string} key - ヘッダーのキー
 * @property {string} label - ヘッダーのラベル
 * @property {string} width - 列幅(px)
 * @property {boolean} [sticky] - 固定列かどうか
 */
type FullHeader = {
  key: string;
  label: string;
  width: string;
  sticky?: boolean;
  resizable?: boolean;
};

/**
 * パフォーマンス最適化されたデフォルトヘッダーキー（一度だけ計算して固定化）
 * 注意: readonlyを使用せずDataCloneErrorを回避
 */
const businessFreeColumnHeaders = computed(() => state.value.businessFreeColumnHeaders);

const defaultFullHeaderKeys = computed(() => {
  const exceptHeaders = new Set(['choice', 'Arrivals', 'orderConfirmation', 'headerModify']);
  const baseHeaderKeys = vm.displayHeaders.inventoryControl.filter((key) => !exceptHeaders.has(key));
  const expandedHeaderKeys = contentVm.replacedHeadersBusinessFreeColumns(baseHeaderKeys, businessFreeColumnHeaders.value);
  const returnHeaders: string[] = ['checkbox', 'orderDetail', ...expandedHeaderKeys];

  // 月次データヘッダー（一度だけ計算）
  const monthlyHeaders = props.transactionMonths.flatMap((month) =>
    inventoryVm.getMonthlyTransactionHeaderKeys(month, false).map((arrivalKey) => `transaction-${month}-${arrivalKey}`)
  );

  returnHeaders.push(...monthlyHeaders, 'headerModify');
  return returnHeaders;
});

const { headerList, updateDisplayHeader } = useTableHeader(
  defaultFullHeaderKeys,
  'inventoryControl',
  inventoryVm.DEFAULT_HIDDEN_HEADER_KEYS
);

/**
 * 完全なヘッダー定義を生成する
 * @returns {FullHeader[]} fullHeaders
 */
const fullHeaders = computed(() => {
  const headerListValue = headerList.value;
  const tableInfos = state.value.tableInfos;

  // 基本ヘッダー定義を最初に作成
  const returnHeaders: FullHeader[] = [
    {
      key: 'checkbox',
      label: '',
      width: '50px',
      sticky: true,
      resizable: false,
    },
    {
      key: 'orderDetail',
      label: t('詳細'),
      width: `${DETAIL_BUTTON_COLUMN_WIDTH}px`,
      sticky: true,
      resizable: false,
    },
  ];

  const exceptHeaderSet = new Set(['choice', 'Arrivals', 'orderConfirmation', 'headerModify']);
  const baseHeaderKeys = vm.displayHeaders.inventoryControl.filter((key) => !exceptHeaderSet.has(key));
  const expandedBaseKeys = contentVm.replacedHeadersBusinessFreeColumns(baseHeaderKeys, businessFreeColumnHeaders.value);
  const baseHeaders = expandedBaseKeys.filter((key) => headerListValue.includes(key));

  // 基本ヘッダーを追加（ヘッダー名のキャッシュ活用）
  for (const header of baseHeaders) {
    let label = headerDisplayCache.get(header);
    if (!label) {
      if (header.startsWith('businessFreeColumnValues-')) {
        label = contentVm.getBusinessFreeColumnHeaderByKey(header, businessFreeColumnHeaders.value)?.headerName ?? '';
      } else {
        label = inventoryVm.getDisplayHeaderName(header);
      }
      if (label) headerDisplayCache.set(header, label);
    }
    if (!label) continue;

    returnHeaders.push({
      key: header,
      label: t(label),
      width: `${tableInfos[header] || MIN_COLUMN_WIDTH}px`,
      sticky: true,
      resizable: true,
    });
  }

  // 月次データヘッダー（事前計算された配列を利用）
  const transactionMonths = props.transactionMonths;
  for (let i = 0; i < transactionMonths.length; i++) {
    const month = transactionMonths[i];
    const monthlyKeys = inventoryVm.getMonthlyTransactionHeaderKeys(month, false);

    for (let j = 0; j < monthlyKeys.length; j++) {
      const arrivalKey = monthlyKeys[j];
      const key = `transaction-${month}-${arrivalKey}`;

      if (!headerListValue.includes(key)) continue;

      const labelMonth = arrivalKey !== 'nowInventory' ? t(DateService.getFormattedMonth(month)) + '\n' : '';

      returnHeaders.push({
        key,
        label: `${labelMonth}${inventoryVm.getTransactionHeaderName(arrivalKey)}`,
        width: `${tableInfos[key] || MIN_COLUMN_WIDTH}px`,
        resizable: arrivalKey !== 'alert',
      });
    }
  }

  // ヘッダー追加列
  returnHeaders.push({
    key: 'headerModify',
    label: t('ヘッダー追加'),
    width: `${HEADER_MODIFY_BUTTON_COLUMN_WIDTH}px`,
    sticky: false,
    resizable: false,
  });

  return returnHeaders;
});

const getModifiableHeaderList = computed(() => {
  const headerListValue = headerList.value;
  const excludedKeys = new Set(['checkbox', 'orderDetail', 'headerModify']);
  const inventoryControlHeaders = new Set(vm.displayHeaders.inventoryControl);

  return defaultFullHeaderKeys.value
    .filter((key) => !excludedKeys.has(key))
    .map((key) => {
      if (key.startsWith('businessFreeColumnValues-')) {
        let text = headerDisplayCache.get(key);
        if (!text) {
          text = contentVm.getBusinessFreeColumnHeaderByKey(key, businessFreeColumnHeaders.value)?.headerName ?? '';
          if (text) headerDisplayCache.set(key, text);
        }
        return { key, text, isShown: headerListValue.includes(key) };
      }

      if (inventoryControlHeaders.has(key)) {
        let text = headerDisplayCache.get(key);
        if (!text) {
          text = inventoryVm.getDisplayHeaderName(key);
          headerDisplayCache.set(key, text);
        }
        return { key, text, isShown: headerListValue.includes(key) };
      }

      const splitKey = key.split('-');
      const arrivalKey = splitKey.at(-1);
      const arrivalMonth = getMonthFromHeaderKey(key);

      if (!splitKey || !arrivalKey) {
        return { key, text: 'ERROR', isShown: headerListValue.includes(key) };
      }

      const labelText = inventoryVm.getTransactionHeaderName(arrivalKey);
      const labelMonth = arrivalKey !== 'nowInventory' ? t(DateService.getFormattedMonth(arrivalMonth)) + '\n' : '';

      return {
        key,
        text: `${labelMonth}${labelText}`,
        isShown: headerListValue.includes(key),
      };
    });
});

/**
 * 固定する列のインデックス配列を生成する
 * @returns {number[]} stickyColumnIndexes
 */
const stickyColumnIndexes = computed(() => {
  const indexes: number[] = [];
  const headers = fullHeaders.value;

  for (let i = 0; i < headers.length; i++) {
    if (headers[i].sticky) {
      indexes.push(i);
    }
  }
  return indexes;
});

/**
 * ヘッダー配列の最適化されたバージョン
 */
const headersDisplay = computed(() => {
  const headers = fullHeaders.value;
  return headers.slice(1); // チェックボックス列を除く
});

/**
 * Setで最適化されたヘッダーソート判定（一度計算してキャッシュ）
 */
const sortableHeaders = computed(() => {
  const sortable = new Set<string>();
  const headers = headersDisplay.value;

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    if (InventoryTableService.isHeaderKeySortable(header.key)) {
      sortable.add(header.key);
    }
  }
  return sortable;
});

/**
 * 最適化されたヘッダーに対する粘着オフセット（前方累積和で計算）
 */
const stickyOffsets = computed(() => {
  const offsets = new Map<string, number>();
  const headers = fullHeaders.value;
  let offset = 0;

  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    if (header.sticky) {
      offsets.set(header.key, offset);
      offset += parseFloat(header.width) || 0;
    }
  }

  return offsets;
});

/**
 * グリッドテンプレート列の配列を生成する（mapの代わりに直接配列構築）
 */
const gridTemplateColumns = computed(() => {
  const headers = fullHeaders.value;
  const columns: string[] = new Array(headers.length);

  for (let i = 0; i < headers.length; i++) {
    columns[i] = headers[i].width;
  }
  return columns;
});

const tableInnerWidth = computed(() => {
  const headers = fullHeaders.value;
  let res = 0;
  for (let i = 0; i < headers.length; i++) {
    res += parseInt(headers[i].width);
  }
  return res;
});

/**
 * 展開されたアイテムのセット
 */
const expandedItems = computed(() => {
  const expanded = new Set<string>();
  if (state.value.isOpeningBusinessId) {
    expanded.add(state.value.isOpeningBusinessId);
  }
  return expanded;
});

/**
 * 子アイテムを取得する関数
 * @param item - 親ビジネスアイテム
 * @returns 子アイテムの配列
 */
const getChildItems = (item: IParentBusiness) => {
  return item.children || [];
};

/**
 * 仮想スクロールの展開/折りたたみハンドラ
 * @param itemId - トグル対象のアイテムID
 * @returns Promise
 */
const handleToggle = async (itemId: string) => {
  await toggleOpenBusinesses(itemId);
};

/**
 * 仮想スクロールのアイテムクリックハンドラ
 */
const handleItemClick = () => {
  // 必要に応じてクリック処理を実装
};

/**
 * 仮想スクロールのスクロールハンドラ
 */
const handleScroll = () => {
  // スクロールイベントの処理（必要に応じて拡張）
  state.value.isOpeningAlertBusinessId = '';
};

/**
 * 横方向スクロールハンドラ
 */
const handleHorizontalScroll = () => {
  state.value.isOpeningAlertBusinessId = '';
};

/**
 * 現在のテーブルローディング状態をEzTable用の型に変換
 */
const currentLoadingStatus = computed((): LoadingStatus => {
  const tableStatus = state.value.tableLoadingState.status;

  if (isLoading(tableStatus) || headerList.value.length === 0) {
    return { type: 'loading' };
  }

  if (tableStatus.type === 'error') {
    return {
      type: 'error',
      errorMessage: tableStatus.errorMessage || t('データの取得に失敗しました。再度お試しください。'),
    };
  }

  if (isZeroMatch(tableStatus) && state.value.businessList.length === 0) {
    return { type: 'zero-match' };
  }

  return { type: 'success' };
});

/**
 * エラー状態での再試行ハンドラ
 */
const handleRetry = () => {
  fetchState();
};

/**
 * YYYY/MM/DD形式のexecutionDateの文字列を取得する（キャッシュ付き）
 * @param business - 取引情報
 */
const getExecutionDateFormatted = (business: IParentBusiness) => {
  const businessId = business.businessId;
  let formatted = executionDateCache.get(businessId);

  if (formatted === undefined) {
    formatted =
      business.executionDate !== undefined
        ? DateService.convertIYearMonthDayToYYYYMMDDWithi18n(DateService.convertDateToIDate(business.executionDate))
        : '';
    executionDateCache.set(businessId, formatted);
  }

  return formatted;
};

/** executionDateのcacheを明示的に更新する */
watch(
  () => state.value.businessRowMenuState.editTransaction.isEditTransactionMode,
  (newValue, oldValue) => {
    if (!newValue && oldValue) {
      executionDateCache.clear();
    }
  }
);

/**
 * 月別の最後のヘッダーキー
 */
const getMonthlyEndHeader = computed(() => {
  return props.transactionMonths.reduce((acc, month) => {
    acc[month] = inventoryVm
      .getMonthlyTransactionHeaderKeys(month, false)
      .filter((key) => !state.value.hiddenTransactionHeaderList.includes(`transaction-${month}-${key}`))
      .at(-1);
    return acc;
  }, {});
});

/**
 * ヘッダーキーがある月の最後のヘッダーと一致しているか(=ヘッダーの右にボーダーが出るか)を返す関数
 * @param headerKey - ヘッダーキー
 */
const borderHeadersSet = computed(() => {
  const borderHeaders = new Set<string>();
  const monthlyEndHeaders = getMonthlyEndHeader.value;

  props.transactionMonths.forEach((month) => {
    const endHeader = monthlyEndHeaders[month];
    if (endHeader) {
      borderHeaders.add(`transaction-${month}-${endHeader}`);
    }
  });

  return borderHeaders;
});

/**
 *　　ヘッダーキーがボーダーの右側にあるかどうかを判定する関数
 * @param headerKey - ヘッダーキー
 */
const hasHeaderRightBorder = (headerKey: string) => borderHeadersSet.value.has(headerKey);

useColumnWidth({
  tableType: 'inventoryControl',
  widthInfo: toRef(state.value, 'tableInfos'),
});
const { onMousedownResizer } = useColumnResizer({
  tableType: 'inventoryControl',
  widthInfo: toRef(state.value, 'tableInfos'),
  tableEl, // テーブル要素のrefを渡す
});

/**
 * 子取引をリロードする(エラー時)
 * @param id - 親取引ID
 */
const reloadChildBusiness = async (id?: string) => {
  if (!id) return;
  await fetchChildBusinesses(id);
};

/**
 * 仮想アイテムの型定義
 */
interface VirtualItem {
  type: 'parent' | 'child' | 'loading' | 'error' | 'zeroMatch';
  data?: IParentBusiness;
  parentId?: string;
}

/**
 * セルがハイライトされているかどうかを判定する
 * @param virtualItem - 仮想アイテム
 * @returns ハイライト状態
 */
const isCellHighlighted = (virtualItem: VirtualItem): boolean => {
  const openingBusinessId = state.value.isOpeningBusinessId;
  const checkedBusinessIds = state.value.checkedAggregateBusinessIds;

  // 早期リターンで計算量を削減
  // 子取引行またはロード行は常にハイライト
  if (virtualItem.type === 'child' || virtualItem.type === 'loading') {
    return true;
  }

  // データが存在しない場合はハイライトしない
  if (!virtualItem.data) {
    return false;
  }

  const itemData = virtualItem.data;

  // チェックボックスが選択されている親取引
  if (checkedBusinessIds.includes(itemData.businessId)) {
    return true;
  }

  // チェックボックスが選択されている子取引（parentIdで判定）
  if (virtualItem.parentId && checkedBusinessIds.includes(virtualItem.parentId)) {
    return true;
  }

  // 展開中の親取引
  if (
    openingBusinessId === itemData.businessId &&
    (itemData.childrenAsyncStatus === 'loading' || itemData.childrenAsyncStatus === 'loaded')
  ) {
    return true;
  }

  return false;
};

/**
 * 固定する列のleft値を算出する（前計算版）
 */
const calcLeftNumber = computed(() => {
  const headers = fullHeaders.value;
  const leftPositions = new Map<number, number>();

  // 前計算で全てのポジションを算出
  let accumulatedLeft = 0;
  for (let i = 0; i < headers.length; i++) {
    if (headers[i].sticky) {
      leftPositions.set(i, accumulatedLeft);
      accumulatedLeft += parseFloat(headers[i].width) || 0;
    } else {
      leftPositions.set(i, 0);
    }
  }

  return (headerIndex: number) => leftPositions.get(headerIndex) || 0;
});

/**
 * 数値編集の値を更新する関数
 * @param value - 数値編集の値
 */
const updateEditTransactionValue = (value: IEditTransactionValue): void => {
  const isValueExists = state.value.businessRowMenuState.editTransaction.changedValue.some(
    (v) => v.month === value.month && v.type === value.type && (value.baseBusinessId ? v.baseBusinessId === value.baseBusinessId : true)
  );
  if (!isValueExists) {
    state.value.businessRowMenuState.editTransaction.changedValue.push(value);
  } else {
    const newChangedValue = state.value.businessRowMenuState.editTransaction.changedValue.map((v) =>
      v.type === value.type && v.month === value.month && (value.baseBusinessId ? v.baseBusinessId === value.baseBusinessId : true)
        ? value
        : v
    );
    state.value.businessRowMenuState.editTransaction.changedValue = newChangedValue;
  }
};

const tableHeaderHeight = computed(() => {
  const tableHeaderContainer = document.getElementsByClassName('table__header-container')[0];
  return tableHeaderContainer && tableHeaderContainer.clientHeight;
});

const editTransactionModeStyle = computed(() => {
  return state.value.businessRowMenuState.editTransaction.isEditTransactionMode
    ? { NOT_ALLOW_CURSOR: 'not-allow-cursor', DISABLED_EVENT: 'disabled-event', BOTH: 'not-allow-cursor disabled-event' }
    : undefined;
});

const tableOverlayTop = computed(
  (): StyleValue => ({
    top: `${
      (tableHeaderHeight.value ?? 0) +
      ITEM_HEIGHT *
        (1 +
          state.value.businessList.length +
          (state.value.businessList.find((b) => state.value.isOpeningBusinessId === b.businessId)?.children.length ?? 0))
    }px`,
  })
);

/**
 * ヘッダーの表示状態を更新する関数
 * ヘッダーの表示状態を更新し、state.value.hiddenTransactionHeaderListを更新する
 * @param key - ヘッダーキー
 * @param isHide - ヘッダーを非表示にするかどうか
 */
const updateTableHeaderData = async (key: string, isHide: boolean): Promise<void> => {
  // ヘッダーの表示状態を更新
  await updateDisplayHeader(key, isHide);
  // 非表示のトランザクションヘッダーリストを更新
  await getHiddenTransactionHeader();
};

/**
 * 言語設定が変更された際にヘッダーのキャッシュをリセットする
 */
watch(
  () => i18n.locale,
  () => {
    headerDisplayCache.clear();
  }
);

/**
 * コンポーネントのクリーンアップ処理
 * メモリリークを防ぐためにキャッシュをクリア
 */
onUnmounted(() => {
  styleClassCache.clear();
  headerDisplayCache.clear();
  executionDateCache.clear();
});
</script>

<template>
  <div class="virtualized-inventory-table-container">
    <VirtualizedEzTable
      :id="TABLE_CONTENT_ID"
      ref="tableEl"
      :items="state.businessList"
      :item-height="ITEM_HEIGHT"
      :container-height="containerHeight"
      :buffer-size="BUFFER_SIZE"
      :grid-template-columns="gridTemplateColumns"
      :expanded-items="expandedItems"
      :get-child-items="getChildItems"
      :loading-status="currentLoadingStatus"
      :sticky-column-indexes="stickyColumnIndexes"
      class="inventory-virtual-table"
      @toggle="handleToggle"
      @item-click="handleItemClick"
      @horizontal-scroll="handleHorizontalScroll"
      @scroll="handleScroll"
      @retry="handleRetry"
    >
      <!-- ヘッダー -->
      <template #header>
        <!-- チェックボックス列 -->
        <EzHeader sticky :sticky-offset="0" :class="editTransactionModeStyle?.NOT_ALLOW_CURSOR">
          <input
            :class="editTransactionModeStyle?.DISABLED_EVENT"
            type="checkbox"
            :checked="state.isCheckAllRows"
            @change="onCheckedAllRows"
          />
        </EzHeader>

        <!-- 各カラムヘッダー -->
        <template v-for="header in headersDisplay" :key="header.key">
          <EzHeader
            v-if="header.key !== 'headerModify'"
            :has-right-border="hasHeaderRightBorder(header.key)"
            :sticky="header.sticky"
            :sticky-offset="stickyOffsets.get(header.key) || 0"
            :use-resizer="header.resizable"
            class="table-header__data"
            @click="changeHeaderSorting(header.key)"
            @resizer="
              (e) => {
                onMousedownResizer(e, header.key);
              }
            "
          >
            <div class="table-header__grid">
              <span class="table-header__data-label">{{ header.label }}</span>
              <ArrowIcon
                v-if="sortableHeaders.has(header.key)"
                :asc="header.key === state.headerSortingState.key ? state.headerSortingState.isAsc : true"
                @on-click="changeHeaderSorting(header.key)"
              />
            </div>
          </EzHeader>
        </template>
        <EzHeader sticky="right">
          <TableHeaderModifyButton
            :modifiable-header-list="getModifiableHeaderList"
            :disabled="state.businessRowMenuState.editTransaction.isEditTransactionMode"
            :tool-tip-message="
              state.businessRowMenuState.editTransaction.isEditTransactionMode ? t('数値データ編集中は使用できません') : undefined
            "
            @toggle="updateTableHeaderData"
          />
        </EzHeader>
      </template>

      <template #body-root>
        <div
          v-if="
            state.businessRowMenuState.editTransaction.isEditTransactionMode &&
            ITEM_HEIGHT * state.businessList.length < tableContainerHeight
          "
          class="edit-transaction-control__overlay--table"
          :style="tableOverlayTop"
        />
      </template>

      <!-- 最上部固定行 -->
      <template #row-top-sticky>
        <div v-if="state.businessRowMenuState.editTransaction.isEditTransactionMode" class="edit-transaction-control__overlay--row" />
        <EzCell
          :style="{
            gridColumn: `1 / ${
              headerList.filter(
                (header) => vm.displayHeaders.inventoryControl.includes(header) || header.startsWith('businessFreeColumnValues-')
              ).length + 2
            }`,
          }"
          class="table-cell--totalization table-cell--totalization--sticky"
        >
          <div class="table-cell--bold">
            {{ t('合計（取引{number}件全ての数値の合計）', { number: String(state.pagingOptions.totalResultsAvailable) }) }}
          </div>
        </EzCell>
        <template v-for="month in props.transactionMonths" :key="month">
          <TransactionSummaryColumn
            :month="month"
            :data="state.summaryData"
            :header-list="headerList"
            :inventory-vm="inventoryVm"
            :monthly-end-header="getMonthlyEndHeader[month]"
          />
        </template>
        <EzCell sticky="right" class="table-cell--totalization" />
      </template>

      <!-- 固定行データ -->
      <template #row-sticky="{ item, virtualItem, handleToggle: rowHandleToggle }">
        <template v-if="virtualItem.type === 'loading'">
          <EzCell is-highlighted :style="{ gridColumn: '1 / -1' }">
            <div class="table-row--child">
              {{ t('ローディング中...') }}
            </div>
          </EzCell>
        </template>
        <template v-else-if="virtualItem.type === 'error'">
          <EzCell class="table-row--error" :style="{ gridColumn: '1 / -1' }">
            <div class="table-row--error__container">
              <div class="table-row--error__message">
                {{ t('子情報の取得に失敗しました。再度お試しください。') }}
              </div>
              <div class="table-row--error__button" @click="reloadChildBusiness(virtualItem.parentId)">
                {{ t('再取得') }}
              </div>
            </div>
          </EzCell>
        </template>
        <template v-else-if="virtualItem.type === 'zeroMatch'">
          <EzCell is-highlighted :style="{ gridColumn: '1 / -1' }">
            <div class="table-row--child">
              {{ t('子取引がありません。') }}
            </div>
          </EzCell>
        </template>
        <template v-else>
          <div
            v-if="
              state.businessRowMenuState.editTransaction.isEditTransactionMode &&
              !(virtualItem.id == state.isOpeningBusinessId || virtualItem.parentId == state.isOpeningBusinessId)
            "
            class="edit-transaction-control__overlay--row"
          />
          <!-- チェックボックスと展開ボタン -->
          <EzCell :is-highlighted="isCellHighlighted(virtualItem)">
            <div class="grid-container table-checkbox" :class="editTransactionModeStyle?.NOT_ALLOW_CURSOR">
              <div v-if="virtualItem.type === 'parent'">
                <input
                  v-model="state.checkedAggregateBusinessIds"
                  :class="editTransactionModeStyle?.DISABLED_EVENT"
                  type="checkbox"
                  :value="item.businessId"
                />
              </div>
              <div v-if="virtualItem.type === 'parent'">
                <input
                  :id="item.businessId"
                  class="table-toggle"
                  :class="editTransactionModeStyle?.DISABLED_EVENT"
                  type="checkbox"
                  @click="rowHandleToggle(item.businessId)"
                />
                <label :for="item.businessId" class="table-toggle__label" :class="editTransactionModeStyle?.BOTH">
                  <SvgLoader v-if="state.isOpeningBusinessId !== item.businessId" class="table-toggle__label--closed" name="chevronRight" />
                  <SvgLoader v-else class="table-toggle__label--opened" name="chevronRight" />
                </label>
              </div>
            </div>
          </EzCell>

          <!-- 詳細ボタン列 -->
          <EzCell class="table-cell__content--detail" :is-highlighted="isCellHighlighted(virtualItem)">
            <router-link
              v-if="virtualItem.type === 'parent'"
              :to="{ name: 'recommend-order', params: { id: item.businessId } }"
              target="_blank"
            >
              <TextButton class="table-button table-button--detail" button-style="btn-secondary" :on-click="() => {}">
                {{ t('詳細') }}
              </TextButton>
            </router-link>
          </EzCell>

          <!-- 全ての列を実装 -->
          <!-- warehouseNumber -->
          <EzCell
            v-if="headerList.includes('warehouseNumber')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.warehouseNumber) : '' }}
          </EzCell>

          <!-- warehouse -->
          <EzCell
            v-if="headerList.includes('warehouse')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.warehouse) : '' }}
          </EzCell>

          <!-- supplierNumber -->
          <EzCell
            v-if="headerList.includes('supplierNumber')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.supplierNumber) : '' }}
          </EzCell>

          <!-- supplier -->
          <EzCell
            v-if="headerList.includes('supplier')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.supplierName) : '' }}
          </EzCell>

          <!-- businessNumber -->
          <EzCell
            v-if="headerList.includes('businessNumber')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.businessNumber) : '' }}
          </EzCell>

          <!-- businessName -->
          <EzCell
            v-if="headerList.includes('businessName')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.businessName) : '' }}
          </EzCell>

          <!-- package -->
          <EzCell
            v-if="headerList.includes('package')"
            :is-number="true"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="
              virtualItem.type === 'child'
                ? 'table-child-item table-cell--number'
                : tableNumberStyleClass(item, state.isOpeningBusinessId, 'virtualized')
            "
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.package) : '' }}
          </EzCell>

          <!-- unit -->
          <EzCell
            v-if="headerList.includes('unit')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.unit) : '' }}
          </EzCell>

          <!-- customerNumber -->
          <EzCell
            v-if="headerList.includes('customerNumber')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ displayOrDash(item.customerNumber) }}
          </EzCell>

          <!-- customerName -->
          <EzCell
            v-if="headerList.includes('customerName')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ displayOrDash(item.customerName) }}
          </EzCell>

          <!-- salesDestinationNumber -->
          <EzCell
            v-if="headerList.includes('salesDestinationNumber')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ displayOrDash(item.salesDestinationNumber) }}
          </EzCell>

          <!-- salesDestinationName -->
          <EzCell
            v-if="headerList.includes('salesDestinationName')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ displayOrDash(item.salesDestinationName) }}
          </EzCell>

          <!-- forecastForReceiptName -->
          <EzCell
            v-if="headerList.includes('forecastForReceiptName')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? '--' : displayOrDash(item.forecastForReceiptName) }}
          </EzCell>

          <!-- manufacturer -->
          <EzCell
            v-if="headerList.includes('manufacturer')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.manufacturer) : '' }}
          </EzCell>

          <!-- endUser -->
          <EzCell
            v-if="headerList.includes('endUser')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ displayOrDash(item.endUser) }}
          </EzCell>

          <!-- purchaseUnitPrice -->
          <EzCell
            v-if="headerList.includes('purchaseUnitPrice')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :is-number="true"
            :class="
              virtualItem.type === 'child'
                ? 'table-child-item table-cell--number'
                : tableNumberStyleClass(item, state.isOpeningBusinessId, 'virtualized')
            "
          >
            {{ virtualItem.type === 'parent' ? item.purchaseUnitPrice?.addComma() ?? '--' : '' }}
          </EzCell>

          <!-- salesUnitPrice -->
          <EzCell
            v-if="headerList.includes('salesUnitPrice')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :is-number="true"
            :class="
              virtualItem.type === 'child'
                ? 'table-child-item table-cell--number'
                : tableNumberStyleClass(item, state.isOpeningBusinessId, 'virtualized')
            "
          >
            {{ item.salesUnitPrice?.addComma() ?? '--' }}
          </EzCell>

          <!-- leadTime -->
          <EzCell
            v-if="headerList.includes('leadTime')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(inventoryVm.getFormattedLeadTime(item.leadTime, item.leadTimeUnit)) : '' }}
          </EzCell>

          <!-- stdInventoryIndex -->
          <EzCell
            v-if="headerList.includes('stdInventoryIndex')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(inventoryVm.getFormattedStdInventoryIndex(item.stdInventoryIndices)) : '' }}
          </EzCell>

          <!-- MOQ -->
          <EzCell
            v-if="headerList.includes('MOQ')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :is-number="true"
            :class="
              virtualItem.type === 'child'
                ? 'table-child-item table-cell--number'
                : tableNumberStyleClass(item, state.isOpeningBusinessId, 'virtualized')
            "
          >
            {{ virtualItem.type === 'parent' ? displayOrDash(item.MOQ) : '' }}
          </EzCell>

          <!-- orderPackage -->
          <EzCell
            v-if="headerList.includes('orderPackage')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :is-number="true"
            :class="
              virtualItem.type === 'child'
                ? 'table-child-item table-cell--number'
                : tableNumberStyleClass(item, state.isOpeningBusinessId, 'virtualized')
            "
          >
            {{ virtualItem.type === 'parent' && item.orderPackage ? item.orderPackage : '--' }}
          </EzCell>

          <!-- supplierForecastResponsibleUser -->
          <EzCell
            v-if="headerList.includes('supplierForecastResponsibleUser')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ virtualItem.type === 'parent' ? inventoryVm.getUserNames(item.aggregateBusinessResponsibleUsers) : '' }}
          </EzCell>

          <!-- customerForecastResponsibleUser -->
          <EzCell
            v-if="headerList.includes('customerForecastResponsibleUser')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ inventoryVm.getUserNames(item.baseBusinessResponsibleUsers) }}
          </EzCell>

          <!-- updatedAt -->
          <EzCell
            v-if="headerList.includes('updatedAt')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ item.updatedAt ? DateService.getDisplayYearMonthDay(new Date(item.updatedAt * 1000)) : '--' }}
          </EzCell>

          <!-- createdAt -->
          <EzCell
            v-if="headerList.includes('createdAt')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')"
          >
            {{ item.createdAt ? DateService.getDisplayYearMonthDay(new Date(item.createdAt * 1000)) : '--' }}
          </EzCell>

          <!-- businessFreeColumnValues (自由欄) -->
          <template v-for="freeColumn in businessFreeColumnHeaders" :key="contentVm.getBusinessFreeColumnHeaderKey(freeColumn)">
            <EzCell
              v-if="headerList.includes(contentVm.getBusinessFreeColumnHeaderKey(freeColumn))"
              :is-highlighted="isCellHighlighted(virtualItem)"
              :class="
                virtualItem.type === 'child' ? 'table-child-item' : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')
              "
            >
              {{
                item.businessFreeColumnValues?.find(
                  (column) =>
                    column.columnNo === freeColumn.columnNo &&
                    (column.target ?? (column.columnNo <= 3 ? 'aggregate' : 'base')) === freeColumn.target
                )?.value || '--'
              }}
            </EzCell>
          </template>

          <!-- memo -->
          <EzCell
            v-if="headerList.includes('memo')"
            :is-highlighted="isCellHighlighted(virtualItem)"
            :class="virtualItem.type === 'child' ? 'table-memo-cell table-child-item' : 'table-memo-cell'"
          >
            <TableMemoButton
              v-if="virtualItem.type === 'parent'"
              :memo="item.note"
              @save="(memoText) => saveAggregateMemo(memoText, item.businessId)"
              @delete="() => deleteAggregateMemo(item.businessId)"
            />
            <TableMemoButton
              v-else
              :memo="item.note"
              @save="(memoText) => saveChildMemo(memoText, item.businessId)"
              @delete="() => deleteChildMemo(item.businessId)"
            />
          </EzCell>
        </template>
      </template>

      <!-- 行データ -->
      <template #row="{ item, virtualItem }">
        <template v-if="virtualItem.type === 'loading' || virtualItem.type === 'zeroMatch'">
          <EzCell is-highlighted />
        </template>
        <template v-else-if="virtualItem.type === 'error'">
          <EzCell class="table-row--error" />
        </template>
        <template v-else>
          <div
            v-if="
              state.businessRowMenuState.editTransaction.isEditTransactionMode &&
              !(virtualItem.id == state.isOpeningBusinessId || virtualItem.parentId == state.isOpeningBusinessId)
            "
            class="edit-transaction-control__overlay--row"
          />
          <!-- 月次データ列 -->
          <template v-for="month in props.transactionMonths" :key="month">
            <InventoryMonthlyColumn
              v-if="virtualItem.type === 'parent' || virtualItem.type === 'child'"
              :month="month"
              :item="item"
              :virtual-item-type="virtualItem.type"
              :monthly-data="state.monthlyData"
              :header-list="headerList"
              :is-opening-business-id="state.isOpeningBusinessId"
              :inventory-vm="inventoryVm"
              :is-highlighted="isCellHighlighted(virtualItem)"
              :monthly-end-header="getMonthlyEndHeader[month]"
              :table-number-style-class="tableNumberStyleClass"
              :get-now-inventory-status-class="getNowInventoryStatusClass"
              :get-execution-date-formatted="getExecutionDateFormatted"
              :is-negative="isNegative"
              :check-nearest-recommend="checkNearestRecommend"
              :get-arrival-status-when-display-button="getArrivalStatusWhenDisplayButton"
              :is-edit-transaction-mode="
                (state.isOpeningBusinessId === item.businessId || state.isOpeningBusinessId === virtualItem.parentId) &&
                state.businessRowMenuState.editTransaction.isEditTransactionMode
              "
              :current-month-edit-transaction-value="
                state.businessRowMenuState.editTransaction.changedValue.filter((v) => v.month === month)
              "
              @update-edit-transaction-value="(val) => updateEditTransactionValue(val)"
            />
          </template>

          <!-- ヘッダー追加列（取引編集ボタン） -->
          <EzCell force-center sticky="right" :is-highlighted="isCellHighlighted(virtualItem)" class="table-cell">
            <BusinessRowMenu
              :is-aggregate-business="virtualItem.type === 'parent'"
              :tool-tip-message="
                state.businessRowMenuState.editTransaction.isEditTransactionMode ? t('数値データ編集中は使用できません') : undefined
              "
              :disabled="state.businessRowMenuState.editTransaction.isEditTransactionMode"
              @select="async (e) => setBusinessRowMenuStateAndProcessing(e, item, virtualItem.type, virtualItem.parentId)"
            />
          </EzCell>
        </template>
      </template>

      <template #loading>
        <EzTableLoadingState :message="t('データを読み込んでいます')" />
      </template>

      <template #error="{ retry }">
        <EzTableErrorState @retry="retry" />
      </template>

      <template #zero-match>
        <EzTableZeroMatchState />
      </template>
    </VirtualizedEzTable>
  </div>
</template>

<style lang="scss" scoped>
@import '@/InventoryControlModule/pages/scss/inventory-table.scss';

.virtualized-inventory-table-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.inventory-virtual-table {
  position: relative;
}

.inventory-table {
  --max-column-width: v-bind('`${MAX_COLUMN_WIDTH}px`');
  --min-column-width: v-bind('`${MIN_COLUMN_WIDTH}px`');
  --loading-grid-column: v-bind('`1 / ${HEADER_COLUMN_DEFAULT_NUMBER - getHiddenHeaderNumber(state.headerList, state.headerList) + 1}`');
}

.table-cell__content--detail {
  justify-content: center;
  :deep(.table__cell__content) {
    padding: 0 8px;
  }
}

.table-button--detail {
  width: 50px;
}

.edit-transaction-control__overlay {
  &--row {
    position: absolute;
    width: v-bind('`${tableInnerWidth}px`');
    height: v-bind('`${ITEM_HEIGHT}px`');
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 5;
  }
  &--table {
    width: v-bind('`${tableInnerWidth}px`');
    height: v-bind('`${tableContainerHeight}px`');
    position: absolute;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 5;
  }
}
</style>
